import React, { useState, useLayoutEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import {
  BookOpen, Fingerprint, Zap, ClipboardCheck, CalendarDays, Menu, Search,
  Bell, HelpCircle, Settings, Check, ChevronLeft, ChevronRight, X,
} from "lucide-react";

const TIP_W = 340;

const isVisible = (sel) => {
  const el = document.querySelector(sel);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

// Passi candidati: quelli con `target` vengono mostrati solo se l'elemento è presente/visibile
// (così il tour si adatta automaticamente a ruolo e dimensione schermo).
const buildCandidates = ({ isApprover }) => {
  const list = [
    { key: "welcome", title: "Benvenuto nel portale Teleborsa HR", body: "Ti mostriamo le funzioni principali in pochi passi. Puoi uscire quando vuoi con \u00abSalta\u00bb.", color: "#16345c", icon: BookOpen },
    { key: "clock", target: '[data-testid="quick-clock-card"]', title: "Timbratura rapida", body: "Registra entrata, pausa e uscita direttamente dalla Home. In fondo vedi la prestazione della giornata.", color: "#009640", icon: Fingerprint },
    { key: "quick", target: '[data-testid="home-quick-access"]', title: "Accesso rapido", body: "Le funzioni che usi pi\u00f9 spesso, sempre a portata di mano. Con \u00abPersonalizza\u00bb scegli quali mostrare.", color: "#0d6cb8", icon: Zap },
  ];
  if (isApprover) {
    list.push({ key: "approv", target: '[data-testid="nav-quick-approvazioni"]', title: "Approvazioni", body: "Le richieste del tuo team da approvare. Il badge rosso indica quante sono in attesa, aggiornato in tempo reale.", color: "#009640", icon: ClipboardCheck });
  } else {
    list.push({ key: "ferie", target: '[data-testid="home-quick-ferie"]', title: "Ferie e Permessi", body: "Richiedi ferie, permessi e altre assenze e controlla i saldi e lo stato di ogni richiesta.", color: "#009640", icon: CalendarDays });
  }
  list.push(
    { key: "macro", target: '[data-testid="nav-macro-menus"]', title: "Tutte le sezioni", body: "Da questi menu raggiungi ogni funzione: Gestione Archivi, Elaborazioni Quotidiane ed Elaborazioni Mensili.", color: "#8e44ad", icon: Menu },
    { key: "search", target: '[data-testid="global-search"]', title: "Ricerca rapida", body: "Non trovi una funzione? Scrivine il nome qui e salti subito alla pagina giusta.", color: "#16345c", icon: Search },
    { key: "bell", target: '[data-testid="notification-bell"]', title: "Notifiche", body: "La campanella ti avvisa di approvazioni, comunicazioni e scadenze importanti.", color: "#e67e22", icon: Bell },
    { key: "help", target: '[data-testid="context-help-btn"]', title: "Aiuto contestuale", body: "Il punto interrogativo apre la Guida della pagina in cui ti trovi.", color: "#8e44ad", icon: HelpCircle },
    { key: "user", target: '[data-testid="header-user-menu"]', title: "Il tuo profilo", body: "Dal tuo nome apri profilo e impostazioni, esci in sicurezza e puoi sempre \u00abRivedere il tour\u00bb.", color: "#607d8b", icon: Settings },
    { key: "end", title: "Tutto qui!", body: "Hai completato il tour. La Guida completa \u00e8 sempre disponibile dal menu in alto.", color: "#009640", icon: Check, isEnd: true },
  );
  return list;
};

const InteractiveTour = ({ isAdmin, isApprover, isExternal, onClose, steps: stepsProp = null, requireAck = true }) => {
  const navigate = useNavigate();
  const [steps] = useState(() => (stepsProp || buildCandidates({ isAdmin, isApprover, isExternal })).filter((s) => !s.target || isVisible(s.target)));
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [tip, setTip] = useState(null);
  const [acked, setAcked] = useState(false);
  const tipRef = useRef(null);

  const step = steps[idx];
  const last = idx === steps.length - 1;
  const Icon = step?.icon || BookOpen;

  // Posiziona lo spotlight sull'elemento target (o nessuno per i passi centrati).
  useLayoutEffect(() => {
    if (!step?.target) { setRect(null); return undefined; }
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return undefined; }
    try { el.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" }); } catch { /* noop */ }
    const update = () => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    update();
    const t = setTimeout(update, 350);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [step]);

  // Calcola la posizione del tooltip in base allo spazio disponibile.
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el) return;
    const th = el.offsetHeight;
    const tw = el.offsetWidth;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (!rect) { setTip({ top: Math.max(16, (vh - th) / 2), left: Math.max(12, (vw - tw) / 2) }); return; }
    const gap = 14;
    let top = rect.top + rect.height + gap;
    if (top + th > vh - 12) top = rect.top - th - gap;
    if (top < 12) top = 12;
    let left = rect.left + rect.width / 2 - tw / 2;
    left = Math.max(12, Math.min(left, vw - tw - 12));
    setTip({ top, left });
  }, [rect, idx]);

  const finishWithAck = useCallback(() => {
    api.post("/tour/ack").catch(() => {});
    onClose();
  }, [onClose]);

  if (!step) return null;

  return (
    <div className="fixed inset-0 z-[9998]" data-testid="interactive-tour">
      {/* Blocca l'interazione con la pagina durante il tour */}
      <div className="absolute inset-0" style={{ background: rect ? "transparent" : "rgba(2,8,23,0.6)" }} onClick={(e) => e.stopPropagation()} />

      {/* Spotlight sull'elemento evidenziato */}
      {rect && (
        <div
          data-testid="tour-spotlight"
          className="pointer-events-none"
          style={{
            position: "fixed",
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            borderRadius: 12,
            boxShadow: "0 0 0 9999px rgba(2,8,23,0.6)",
            border: "2px solid #ff7e00",
            transition: "top .2s ease, left .2s ease, width .2s ease, height .2s ease",
          }}
        />
      )}

      {/* Tooltip / pop-up del passo */}
      <div
        ref={tipRef}
        data-testid="welcome-tour"
        className="fixed bg-white rounded-xl shadow-2xl overflow-hidden"
        style={{ width: `min(${TIP_W}px, calc(100vw - 24px))`, top: tip?.top ?? -9999, left: tip?.left ?? -9999, opacity: tip ? 1 : 0, transition: "top .2s ease, left .2s ease, opacity .15s ease", zIndex: 10000 }}
      >
        <div className="h-1.5" style={{ backgroundColor: step.color }} />
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${step.color}1a`, color: step.color }}>
              <Icon size={22} />
            </span>
            <button onClick={onClose} data-testid="tour-skip" className="text-[12px] font-semibold text-gray-400 hover:text-gray-600 flex items-center gap-1">
              <X size={14} /> Salta
            </button>
          </div>

          <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1">Passo {idx + 1} di {steps.length}</div>
          <h2 className="text-[18px] font-black text-[#16345c] mb-1.5" data-testid={`tour-step-${step.key}`}>{step.title}</h2>
          <p className="text-[13.5px] text-gray-600 leading-relaxed min-h-[64px]">{step.body}</p>

          <div className="flex items-center justify-center gap-1.5 my-4">
            {steps.map((_, k) => (
              <span key={k} className={`h-1.5 rounded-full transition-all ${k === idx ? "w-5 bg-[#16345c]" : "w-1.5 bg-gray-300"}`} />
            ))}
          </div>

          {last && requireAck && (
            <label className="flex items-start gap-2 mb-4 cursor-pointer select-none bg-gray-50 border border-gray-200 rounded-sm px-3 py-2.5" data-testid="tour-ack-checkbox">
              <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} data-testid="tour-ack-input" className="mt-0.5 w-4 h-4 accent-[#16345c]" />
              <span className="text-[12.5px] text-gray-700 leading-snug">Confermo di aver preso visione del tour di benvenuto.</span>
            </label>
          )}

          <div className="flex items-center justify-between gap-2">
            <button onClick={() => setIdx((v) => Math.max(0, v - 1))} disabled={idx === 0} data-testid="tour-prev"
              className="flex items-center gap-1 text-[13px] font-semibold text-gray-600 px-3 py-2 rounded-sm hover:bg-gray-100 disabled:opacity-0 transition-colors">
              <ChevronLeft size={15} /> Indietro
            </button>
            {last ? (
              requireAck ? (
                <div className="flex items-center gap-2">
                  <button onClick={() => { finishWithAck(); navigate("/guida"); }} disabled={!acked} data-testid="tour-open-guide"
                    className="text-[13px] font-bold text-[#16345c] px-3 py-2 rounded-sm hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Apri la guida</button>
                  <button onClick={finishWithAck} disabled={!acked} data-testid="tour-finish"
                    className="flex items-center gap-1.5 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <Check size={15} /> Fine
                  </button>
                </div>
              ) : (
                <button onClick={onClose} data-testid="tour-finish"
                  className="flex items-center gap-1.5 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors">
                  <Check size={15} /> Fine
                </button>
              )
            ) : (
              <button onClick={() => setIdx((v) => Math.min(steps.length - 1, v + 1))} data-testid="tour-next"
                className="flex items-center gap-1.5 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors">
                Avanti <ChevronRight size={15} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveTour;
